import {
  createContext,
  FunctionComponent,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ThemeProvider } from "@mui/material";

import useAuthSnapshot from "../hooks/useAuthSnapshot";
import { GlobalStyles } from "../components/styles";
import { createAppTheme } from "../components/theme";
import {
  applyThemeMode,
  getThemeIdentity,
  persistThemeMode,
  readThemeMode,
} from "./themeMode";
import type { ThemeMode } from "./tokens";

type ThemeModeContextValue = {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
};

const ThemeModeContext = createContext<ThemeModeContextValue | null>(null);

export const useThemeMode = (): ThemeModeContextValue => {
  const value = useContext(ThemeModeContext);
  if (!value) {
    throw new Error("useThemeMode must be used inside ThemeModeProvider");
  }
  return value;
};

export const AppThemeProvider: FunctionComponent<{ children: ReactNode }> = ({
  children,
}) => {
  const { userData } = useAuthSnapshot();
  const identity = useMemo(() => getThemeIdentity(userData), [
    userData?.id,
    userData?.user_id,
    userData?.email,
    userData?.tlc,
  ]);
  const [mode, setMode] = useState<ThemeMode>(() => readThemeMode(identity));

  useEffect(() => {
    setMode(readThemeMode(identity));
  }, [identity]);

  useEffect(() => {
    applyThemeMode(mode);
    persistThemeMode(identity, mode);
  }, [identity, mode]);

  const value = useMemo<ThemeModeContextValue>(
    () => ({
      mode,
      setMode,
      toggleMode: () => setMode((current) => (current === "dark" ? "light" : "dark")),
    }),
    [mode],
  );
  const muiTheme = useMemo(() => createAppTheme(mode), [mode]);

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={muiTheme}>
        <GlobalStyles />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};
